import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Banknote, BellOff, CheckCircle2, History, Loader2, PhoneCall, RefreshCw, Search, ShieldAlert, Siren, Timer, X } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import { messageErreur } from '../lib/erreurs';
import { accorde } from '../lib/formats';
import { libelle, texteSur } from '../lib/libelles';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import { AlertRow, AlertDetail, alertTone } from '../components/alerts/AlertWorkspace';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import './alerts-workspace.css';

const categories = ['Toutes', 'GPS', 'Geofence', 'Workflow', 'Finance', 'Documents', 'Stock', 'Plateforme'];
const libelleCategorie = (category) => category === 'Toutes' ? 'Toutes les catégories' : libelle('alerteCategorie', category, 'Autres');

const views = [
  { key: 'open', label: 'À traiter', icon: ShieldAlert },
  { key: 'acknowledged', label: 'En cours', icon: Timer },
  { key: 'resolved', label: 'Historique', icon: History },
];

const decisions = [
  {
    type: 'call_driver',
    label: 'Noter un appel chauffeur',
    icon: PhoneCall,
    comment: 'Appel du chauffeur demandé depuis les alertes',
    toast: 'Appel chauffeur noté — l’alerte passe « En cours ».',
  },
  {
    type: 'declare_incident',
    label: 'Incident',
    icon: Siren,
    comment: 'Incident déclaré depuis les alertes',
    toast: 'Incident déclaré — l’alerte passe « En cours ».',
  },
  {
    type: 'assign_finance_review',
    label: 'Confier à la finance',
    icon: Banknote,
    comment: 'Dossier confié à la finance depuis les alertes',
    toast: 'Confié à la finance — l’alerte passe « En cours ».',
  },
  {
    type: 'ignore',
    label: 'Ignorer',
    icon: BellOff,
    comment: 'Alerte ignorée après revue opérateur',
    toast: 'Alerte ignorée — la décision reste tracée dans l’historique.',
  },
  {
    type: 'resolve',
    label: 'Résoudre',
    icon: CheckCircle2,
    variant: 'default',
    comment: 'Alerte résolue depuis les alertes',
    toast: 'Alerte résolue — elle rejoint l’historique.',
  },
];


// [catégorie, cause, action]. Le titre vient de `libelle('alerteEvent', code)`.
const translations = {
  tracking_conflict: ['GPS', 'Deux sources ne donnent pas la même position.', 'Vérifier le boîtier, le téléphone du chauffeur et le dernier point fiable.'],
  trip_status_proposed: ['Workflow', 'Une étape du trajet est proposée, par la position du camion ou par le chauffeur.', 'Confirmer l’étape si elle est exacte.'],
  geo_location_entered: ['Geofence', 'Le camion est entré dans une zone suivie.', 'Contrôler si l’arrivée était attendue et prévenir l’équipe concernée.'],
  geo_location_exited: ['Geofence', 'Le camion a quitté une zone suivie.', 'Vérifier la suite du trajet et les documents associés.'],
  reconciliation_blocking_variance: ['Finance', 'Un écart de carburant empêche de fiabiliser le montant.', 'Comparer les volumes chargés, livrés et rapprochés.'],
  workflow_blocked: ['Workflow', 'Une étape du trajet ne peut pas avancer.', 'Ouvrir le trajet et traiter le blocage indiqué.'],
  document_ocr_failed: ['Documents', 'Le texte du document n’a pas pu être lu correctement.', 'Relancer la lecture du document ou vérifier le fichier enregistré.'],
  low_stock: ['Stock', 'Le niveau de stock est proche du seuil ou en dessous.', 'Prévoir un réapprovisionnement ou réduire les sorties.'],
  openwa_session_down: ['Plateforme', 'La session WhatsApp est coupée.', 'Vérifier la session dans OpenWA ; si la relance automatique a échoué, la relancer ou reconnecter le téléphone.'],
  openwa_webhook_mismatch: ['Plateforme', 'L’adresse où OpenWA envoie les accusés et les messages reçus n’est pas celle d’E-Carbu.', 'Corriger l’adresse de réception dans OpenWA.'],
  openwa_acks_silent: ['Plateforme', 'OpenWA n’a renvoyé aucun accusé de réception depuis plus de 30 minutes.', 'Vérifier l’adresse de réception dans OpenWA.'],
  openwa_queue_stalled: ['Plateforme', 'Des messages attendent depuis plus de 5 minutes alors que la session est prête.', 'Vérifier que le service d’envoi d’E-Carbu tourne.'],
  openwa_canary_failed: ['Plateforme', 'Le message de contrôle quotidien n’est pas arrivé.', 'Vérifier la session, l’adresse de réception et le téléphone de contrôle.'],
  traccar_unreachable: ['Plateforme', 'Le serveur GPS ne répond plus : aucune position de boîtier n’arrive.', 'Vérifier que le serveur GPS tourne ; les boîtiers renvoient leurs points mémorisés à son retour.'],
  traccar_forward_silent: ['Plateforme', 'Le serveur GPS reçoit des positions mais E-Carbu n’en reçoit plus de lui.', 'Vérifier l’adresse de transfert et le secret de Traccar, la fiche « Pont Traccar » et les IMEI déclarés.'],
  traccar_no_device: ['Plateforme', 'Des boîtiers sont déclarés mais aucun n’est en ligne depuis plus de 30 minutes.', 'Vérifier l’alimentation et la SIM des boîtiers, les ports ouverts et le nom de serveur réglé.'],
};

/** Titre affiché: le libellé connu du type d’alerte, sinon le titre envoyé, sinon un titre simple. */
const titreAlerte = (event, key, repli) =>
  libelle('alerteEvent', key, texteSur(event.title, repli));

const infer = (event) => {
  const key = event.event_type || event.type || event.code || '';
  const text = `${key} ${event.title || ''} ${event.message || ''}`.toLowerCase();
  const serverContext = event.business_context || {};
  const translated = translations[key];
  const withServer = (fallback) => ({
    ...fallback,
    cause: texteSur(serverContext.cause, fallback.cause),
    impact: texteSur(serverContext.impact || fallback.impact || event.message || event.title, 'Risque à suivre pour vos opérations.'),
    action: texteSur(serverContext.recommended_action, fallback.action),
  });
  const message = (repli) => texteSur(event.message, repli);
  if (translated) return withServer({ key, category: translated[0], label: titreAlerte(event, key, 'Alerte'), cause: translated[1], action: translated[2] });
  if (text.includes('geo')) return withServer({ key, category: 'Geofence', label: titreAlerte(event, key, 'Alerte de zone'), cause: message('Mouvement détecté dans une zone suivie.'), action: 'Contrôler le camion et la zone concernée.' });
  if (text.includes('gps') || text.includes('tracking')) return withServer({ key, category: 'GPS', label: titreAlerte(event, key, 'Alerte de suivi GPS'), cause: message('Signal GPS à vérifier.'), action: 'Comparer les sources et la fraîcheur de la dernière position.' });
  if (text.includes('workflow')) return withServer({ key, category: 'Workflow', label: titreAlerte(event, key, 'Alerte sur les étapes du trajet'), cause: message('Étape bloquée ou incomplète.'), action: 'Ouvrir le trajet concerné.' });
  if (text.includes('finance') || text.includes('reconciliation') || text.includes('variance')) return withServer({ key, category: 'Finance', label: titreAlerte(event, key, 'Alerte sur l’argent'), cause: message('Montant ou volume à contrôler.'), action: 'Vérifier le rapprochement des volumes.' });
  if (text.includes('document') || text.includes('ocr')) return withServer({ key, category: 'Documents', label: titreAlerte(event, key, 'Alerte sur un document'), cause: message('Document à vérifier.'), action: 'Ouvrir les documents.' });
  if (text.includes('stock')) return withServer({ key, category: 'Stock', label: titreAlerte(event, key, 'Alerte de stock'), cause: message('Stock à surveiller.'), action: 'Contrôler les mouvements de stock.' });
  return withServer({ key, category: 'Workflow', label: titreAlerte(event, key, 'Alerte'), cause: message('Situation à traiter.'), action: 'Ouvrir l’élément concerné et traiter la situation.' });
};

/**
 * Où mène une alerte: la fiche de l'entité qu'elle concerne.
 *
 * Une échéance de papier ouvre l'onglet Documents du camion; toute autre
 * alerte camion, son aperçu. Un convoi mène à sa fiche. Sans entité, pas de
 * lien: mieux vaut aucun bouton qu'un bouton qui mène nulle part.
 */
export const alertTarget = (event) => {
  const id = event?.entity_id;
  if (!id) return null;
  if (event.entity_type === 'truck') {
    const tab =
      event.event_type === 'VEHICLE_DOCUMENT_EXPIRY'
        ? 'documents'
        : event.event_type === 'MAINTENANCE_PLAN_DUE'
          ? 'maintenance'
          : 'apercu';
    return { path: `/trucks/${id}?tab=${tab}`, label: 'Voir le camion' };
  }
  if (event.entity_type === 'trip') {
    return { path: `/trips/${id}`, label: 'Voir le trajet' };
  }
  if (event.entity_type === 'operation') {
    return { path: `/operations/${id}`, label: 'Voir le convoi' };
  }
  return null;
};

const emptyCounts = { to_handle: 0, in_progress: 0, handled: 0, escalated: 0, critical_to_handle: 0 };

export default function AlertsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState(emptyCounts);
  const [hasSummary, setHasSummary] = useState(false);
  const [view, setView] = useState('open');
  const [activeCategory, setActiveCategory] = useState('Toutes');
  const [priority, setPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [decisionError, setDecisionError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
  const requestRef = useRef(0);
  const viewRef = useRef(view);
  const mutationRef = useRef(false);
  const refreshRef = useRef(false);
  const selectedButton = useRef(null);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const load = useCallback(async (nextView, { silencieux = false } = {}) => {
    if (nextView !== viewRef.current) return false;
    const request = ++requestRef.current;
    if (!silencieux) setLoading(true);
    setLoadError(null);
    try {
      const [list, summary] = await Promise.all([
        api.get(`/alerts/events?status=${nextView}`), api.get('/alerts/events/summary'),
      ]);
      if (request !== requestRef.current || nextView !== viewRef.current) return false;
      setEvents(Array.isArray(list.data) ? list.data : []);
      setCounts({ ...emptyCounts, ...(summary.data || {}) });
      setHasSummary(true);
      setUpdatedAt(new Date());
      return true;
    } catch (error) {
      if (request === requestRef.current && nextView === viewRef.current) {
        setLoadError(messageErreur(error, 'Impossible de charger les alertes.'));
      }
      return false;
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(view);
    return () => { requestRef.current += 1; };
  }, [load, view]);
  useRafraichirSur(['alertes'], () => mutationRef.current ? undefined : load(viewRef.current, { silencieux: true }));

  const enriched = useMemo(() => events.map((event) => ({ ...event, ui: infer(event) })), [events]);
  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('fr');
    return enriched.filter((event) => {
      if (activeCategory !== 'Toutes' && event.ui.category !== activeCategory) return false;
      if (priority === 'critical' && alertTone(event) !== 'critical') return false;
      if (priority === 'escalated' && !event.escalated) return false;
      if (priority === 'attention' && alertTone(event) !== 'attention') return false;
      const haystack = `${event.ui.label} ${libelleCategorie(event.ui.category)} ${event.ui.cause} ${event.title || ''} ${event.message || ''}`;
      return !term || haystack.toLocaleLowerCase('fr').includes(term);
    });
  }, [enriched, activeCategory, priority, search]);
  const pagination = usePagination(visible, { reinitialiser: `${view}|${activeCategory}|${priority}|${search}` });
  const explicitSelection = pagination.lignes.find((event) => event.id === selectedId);
  const selected = explicitSelection || (!mobile ? pagination.lignes[0] : null);
  const filtered = activeCategory !== 'Toutes' || priority !== 'all' || !!search.trim();

  const changeView = (nextView) => {
    if (nextView === viewRef.current) return;
    viewRef.current = nextView;
    requestRef.current += 1;
    setEvents([]);
    setLoading(true);
    setLoadError(null);
    setView(nextView);
    setActiveCategory('Toutes');
    setPriority('all');
    setSelectedId(null);
    setDecisionError(null);
  };

  const refresh = async () => {
    if (refreshRef.current || mutationRef.current) return;
    refreshRef.current = true;
    setRefreshing(true);
    try {
      await api.post('/alerts/refresh');
      if (await load(viewRef.current, { silencieux: true })) toast.success('Alertes recalculées');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de recalculer les alertes.'));
    } finally {
      refreshRef.current = false;
      setRefreshing(false);
    }
  };

  const decide = async (event, decision, note) => {
    if (mutationRef.current || refreshRef.current) return;
    mutationRef.current = true;
    requestRef.current += 1;
    setBusyId(event.id);
    setDecisionError(null);
    try {
      await api.post(`/alerts/events/${event.id}/decision`, {
        decision_type: decision.type,
        comment: note.trim() || decision.comment,
        decision_source: 'web',
      });
      setEvents((current) => current.filter((row) => row.id !== event.id));
      toast.success(decision.toast);
      await load(viewRef.current, { silencieux: true });
    } catch (error) {
      setDecisionError({ id: event.id, message: messageErreur(error, 'La décision n’a pas pu être confirmée. Vérifiez le dossier avant de réessayer.') });
      // Relire est sûr ; ne jamais réémettre automatiquement une décision.
      await load(viewRef.current, { silencieux: true });
    } finally {
      mutationRef.current = false;
      setBusyId(null);
    }
  };

  // Une étape proposée par une zone ou par le chauffeur: un geste, et le trajet avance.
  const confirmStep = async (event) => {
    if (mutationRef.current || refreshRef.current) return;
    mutationRef.current = true;
    requestRef.current += 1;
    setBusyId(event.id);
    setDecisionError(null);
    try {
      await api.post(`/trips/${event.entity_id}/geofence-proposals/${event.id}/confirm`);
      setEvents((current) => current.filter((row) => row.id !== event.id));
      toast.success('Étape confirmée — le trajet a avancé.');
      await load(viewRef.current, { silencieux: true });
    } catch (error) {
      setDecisionError({ id: event.id, message: messageErreur(error, 'L’étape n’a pas pu être confirmée.') });
      await load(viewRef.current, { silencieux: true });
    } finally {
      mutationRef.current = false;
      setBusyId(null);
    }
  };

  const resetFilters = () => { setSearch(''); setActiveCategory('Toutes'); setPriority('all'); };
  const select = (id, button) => { selectedButton.current = button; setSelectedId(id); setDecisionError(null); };
  const countForView = (key) => !hasSummary ? '—' : key === 'open' ? counts.to_handle : key === 'acknowledged' ? counts.in_progress : counts.handled;
  const detail = selected && <AlertDetail key={selected.id} event={selected} view={view} decisions={decisions}
    busy={busyId !== null || refreshing} error={decisionError?.id === selected.id ? decisionError.message : null}
    target={alertTarget(selected)} onOpenTarget={() => navigate(alertTarget(selected).path, { state: { from: '/alerts' } })} onDecide={decide} onConfirm={confirmStep} />;

  return (
    <div className="alerts-workspace mx-auto w-full max-w-page px-4 py-3 md:px-6 lg:px-8">
      <PageHeader eyebrow="Centre de suivi" title="Alertes"
        description="Repérez ce qui demande votre attention, consultez le contexte et décidez de la suite."
        stats={[
          { label: 'Critiques à traiter', valeur: hasSummary ? counts.critical_to_handle : null, ton: counts.critical_to_handle ? 'critique' : 'neutre' },
          { label: 'Escaladées', valeur: hasSummary ? counts.escalated : null, ton: counts.escalated ? 'attention' : 'neutre', aide: 'Alertes remontées après une attente prolongée' },
        ]}
        actions={<Button variant="outline" onClick={refresh} disabled={loading || refreshing || busyId !== null} title="Recalculer les alertes de votre société">
          <RefreshCw size={16} className={cn('mr-2', refreshing && 'animate-spin')} aria-hidden="true" />Recalculer
        </Button>}
      />
      <div className="alert-inbox-shell">
        <div className="alert-workspace-toolbar">
          <Tabs value={view} onValueChange={changeView}>
            <TabsList aria-label="État des alertes" className="alert-view-tabs">
              {views.map(({ key, label, icon: Icon }) => <TabsTrigger key={key} value={key} id={`alerts-tab-${key}`} aria-controls="alerts-inbox-view" className="alert-view-tab">
                <Icon size={16} aria-hidden="true" /><span>{label}</span><span className="alert-view-count">{countForView(key)}</span>
              </TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="alert-search">
            <Search size={16} aria-hidden="true" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Camion, sujet, mot-clé…" aria-label="Rechercher une alerte" />
            {search && <button type="button" onClick={() => setSearch('')} aria-label="Effacer la recherche"><X size={15} /></button>}
          </div>
        </div>
        <div className="alert-filterbar">
          <label className="alert-filter"><span className="sr-only">Catégorie</span>
            <select aria-label="Filtrer par catégorie" value={activeCategory} onChange={(e) => setActiveCategory(e.target.value)}>
              {categories.map((category) => <option key={category} value={category}>{libelleCategorie(category)}</option>)}
            </select>
          </label>
          <label className="alert-filter"><span className="sr-only">Priorité</span>
            <select aria-label="Filtrer par priorité" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="all">Toutes les priorités</option><option value="critical">Critiques</option><option value="attention">À surveiller</option><option value="escalated">Escaladées</option>
            </select>
          </label>
          {filtered && <Button variant="ghost" size="sm" onClick={resetFilters}>Réinitialiser<X size={14} className="ml-1" aria-hidden="true" /></Button>}
          <span className="alert-sync" role="status">
            {loading ? <><Loader2 size={13} className="animate-spin" aria-hidden="true" /> Chargement…</> : loadError ? 'Actualisation indisponible' : updatedAt ? `À jour à ${updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </div>
        {loadError && <div className="alert-load-error" role="alert"><div><strong>Les alertes n’ont pas pu être actualisées.</strong><p>{loadError} {events.length > 0 && 'Les données affichées peuvent être anciennes.'}</p></div>
          <Button variant="outline" size="sm" onClick={() => load(view)} disabled={loading}>Réessayer</Button>
        </div>}
        <div className="alert-workspace-grid" id="alerts-inbox-view" role="tabpanel" aria-labelledby={`alerts-tab-${view}`} tabIndex={0} aria-busy={loading}>
          <section className="alert-inbox" aria-label="Liste des alertes">
            <div className="alert-list-heading"><h2>{views.find((v) => v.key === view).label}</h2><span>{!loading && !loadError ? accorde(visible.length, 'alerte', 'alertes') : '—'}{filtered && ' · filtres actifs'}</span></div>
            {loading && events.length === 0 ? <div className="alert-skeleton" role="status" aria-label="Chargement des alertes">{[0, 1, 2, 3].map((i) => <div key={i}><span /><span /><span /></div>)}</div>
              : visible.length > 0 ? <><ul className="alert-inbox-list">{pagination.lignes.map((event) => <AlertRow key={event.id} event={event} selected={event.id === selected?.id} onSelect={select} />)}</ul>
                <BarrePagination pagination={pagination} nom="alertes" className="alert-pagination" /></>
                : !loadError && <EmptyState variant={filtered ? 'filtered' : 'empty'} illustration={filtered ? undefined : view === 'open' ? 'ajour' : undefined}
                  icon={filtered ? Search : view === 'resolved' ? History : CheckCircle2}
                  title={filtered ? 'Aucune alerte correspondante' : view === 'open' ? 'Vous êtes à jour' : view === 'acknowledged' ? 'Aucune alerte en cours' : 'Aucune alerte clôturée'}
                  description={filtered ? 'Ajustez les filtres ou cherchez un autre mot-clé.' : view === 'open' ? 'Les prochaines alertes à traiter apparaîtront ici.' : 'Les changements de statut apparaîtront dans cet espace.'}
                  actionLabel={filtered ? 'Effacer les filtres' : undefined} onAction={filtered ? resetFilters : undefined} />}
          </section>
          {!mobile && <aside className="alert-detail" aria-label="Détail de l’alerte">{detail || <div className="alert-detail-placeholder"><span className="alert-placeholder-symbol"><ShieldAlert size={28} aria-hidden="true" /></span><h2>Le contexte, au bon endroit</h2><p>Sélectionnez une alerte pour comprendre la situation et décider de la suite.</p></div>}</aside>}
        </div>
      </div>
      {mobile && <Sheet open={!!explicitSelection} onOpenChange={(open) => { if (!open) setSelectedId(null); }}>
        <SheetContent side="right" className="alert-mobile-sheet" onCloseAutoFocus={(e) => { e.preventDefault(); if (selectedButton.current?.isConnected) selectedButton.current.focus(); }}>
          <SheetTitle className="sr-only">Détail de l’alerte</SheetTitle>
          <SheetDescription className="sr-only">Contexte et décisions autorisées pour l’alerte sélectionnée.</SheetDescription>
          {detail}
        </SheetContent>
      </Sheet>}
    </div>
  );
}
